import type {
  SolarConsumptionGreenButtonBlockedReason,
  SolarConsumptionInputMode,
  SolarConsumptionMonthBasis,
  SolarConsumptionSelectedDto,
  UpdateSolarProposalConsumptionDto,
} from "~/api/dto/solar-proposal-consumption.dto"

export const MONTH_LABELS = [
  `Jan`,
  `Feb`,
  `Mar`,
  `Apr`,
  `May`,
  `Jun`,
  `Jul`,
  `Aug`,
  `Sep`,
  `Oct`,
  `Nov`,
  `Dec`,
] as const

export const CONSUMPTION_SAVED_MESSAGE = `Saved. Savings are now out of date.`
export const CONSUMPTION_SAVE_FAILED_MESSAGE = `Couldn't save usage.`
export const GREEN_BUTTON_AVAILABLE_MESSAGE = `Green Button data is available for this home.`
export const GREEN_BUTTON_NEWER_MESSAGE = `Newer Green Button data is available.`
export const GREEN_BUTTON_CONNECTED_MESSAGE = `This home has a Green Button connection.`
export const GREEN_BUTTON_STEP_LINK_LABEL = `Open Green Button Data`
export const GREEN_BUTTON_OVERRIDE_WARNING = `Saving manual usage replaces the Green Button data for this proposal. You can switch back from Green Button Data.`
export const LEGACY_SOURCE_LINE = `From an earlier version of this proposal`

const BASIS_LABELS: Record<SolarConsumptionMonthBasis, string> = {
  seller_entered: `Entered`,
  vision_modeled: `Estimated`,
  provider_modeled: `Estimated`,
  measured: `Measured`,
  observed_modeled: `Modeled`,
}

export const monthBasisLabel = (basis: SolarConsumptionMonthBasis): string =>
  BASIS_LABELS[basis]

const BLOCKED_REASON_MESSAGES: Record<
  SolarConsumptionGreenButtonBlockedReason,
  string
> = {
  grid_import_basis: `Grid-import data from an existing solar system can't be used as household usage.`,
  incoherent_summaries: `This home's Green Button data doesn't add up, so it can't be used as household usage.`,
  not_measured: `This home's Green Button data doesn't cover a full measured year yet.`,
}

export const greenButtonBlockedMessage = (
  reason: SolarConsumptionGreenButtonBlockedReason,
): string => BLOCKED_REASON_MESSAGES[reason]

/** One line naming where the usage came from, as a seller would say it. */
export const consumptionSourceLine = (
  selected: SolarConsumptionSelectedDto,
): string => {
  switch (selected.source) {
    case `manual`:
      return selected.inputMode === `monthly`
        ? `Entered by seller · Monthly`
        : selected.inputMode === `annual`
          ? `Entered by seller · Annual total, months estimated from it`
          : `Entered by seller`
    case `green_button`:
      if (selected.measurementBasis === `GRID_IMPORT`) {
        return `Grid import only (existing solar)`
      }
      return selected.quality === `MEASURED`
        ? `Green Button · measured${
            selected.coveragePercent == null
              ? ``
              : ` · ${Math.round(selected.coveragePercent)}% coverage`
          }`
        : `Green Button · partial year, modeled`
    case `aurora`:
      return `From the Aurora design`
    case `legacy`:
      return LEGACY_SOURCE_LINE
  }
}

export type ConsumptionDraft = {
  mode: SolarConsumptionInputMode
  annualText: string
  monthlyTexts: string[]
}

const kwhText = (kwh: number | null | undefined): string =>
  kwh == null || !Number.isFinite(kwh) ? `` : String(Math.round(kwh))

/**
 * The form as the server's values fill it: monthly when the seller last
 * entered months, otherwise an annual total. Values show as whole kWh.
 */
export const initialConsumptionDraft = (
  selected: SolarConsumptionSelectedDto | null,
): ConsumptionDraft => ({
  mode: selected?.inputMode === `monthly` ? `monthly` : `annual`,
  annualText: kwhText(selected?.annualKwh),
  monthlyTexts:
    selected?.months.length === 12
      ? selected.months.map(({ kwh }) => kwhText(kwh))
      : MONTH_LABELS.map(() => ``),
})

const parseKwh = (text: string): number | null => {
  const trimmed = text.trim().replaceAll(`,`, ``)
  if (trimmed === ``) return null
  const value = Number(trimmed)
  return Number.isFinite(value) ? value : null
}

export type ConsumptionFormState = {
  changed: boolean
  valid: boolean
  body: UpdateSolarProposalConsumptionDto | null
  /** kWh, never money; null until every month parses. */
  monthlyTotalPreview: number | null
  errors: {
    annual: string | null
    months: Array<string | null>
    total: string | null
  }
}

/**
 * A field's kWh: the server's own unrounded value while the seller leaves the
 * prefilled text alone, so re-saving never stores the display's rounding;
 * otherwise what they typed.
 */
const fieldKwh = (
  text: string,
  initialText: string,
  original: number | null | undefined,
): number | null =>
  original != null && text.trim() === initialText.trim()
    ? original
    : parseKwh(text)

/**
 * Validates the draft as the server will: an annual total of at least 1 kWh,
 * or twelve months of at least 0 kWh that add up to at least 1 kWh. Only the
 * chosen mode counts. A form whose values match the saved ones (however
 * they're typed) is unchanged and never saveable.
 */
export const consumptionFormState = (
  draft: ConsumptionDraft,
  selected: SolarConsumptionSelectedDto | null,
): ConsumptionFormState => {
  const initial = initialConsumptionDraft(selected)
  const modeChanged = draft.mode !== initial.mode

  if (draft.mode === `annual`) {
    const annualKwh = fieldKwh(
      draft.annualText,
      initial.annualText,
      selected?.annualKwh,
    )
    const changed = modeChanged || annualKwh !== (selected?.annualKwh ?? null)
    const annualError =
      annualKwh == null || annualKwh < 1 ? `Enter at least 1 kWh.` : null
    return {
      changed,
      valid: annualError === null,
      body:
        annualError === null && annualKwh != null
          ? { mode: `annual`, annualKwh }
          : null,
      monthlyTotalPreview: null,
      errors: {
        annual: draft.annualText.trim() === `` && !changed ? null : annualError,
        months: MONTH_LABELS.map(() => null),
        total: null,
      },
    }
  }

  const savedMonths =
    selected?.months.length === 12 ? selected.months.map(({ kwh }) => kwh) : null
  const values = draft.monthlyTexts.map((text, month) =>
    fieldKwh(text, initial.monthlyTexts[month], savedMonths?.[month]),
  )
  const changed =
    modeChanged ||
    savedMonths === null ||
    values.some((value, month) => value !== savedMonths[month])
  // An empty month blocks saving without an error of its own until typed in.
  const monthErrors = draft.monthlyTexts.map((text, month) => {
    const value = values[month]
    return text.trim() !== `` && (value == null || value < 0)
      ? `Enter 0 or more kWh.`
      : null
  })
  const parsed = values.every(
    (value): value is number => value != null && value >= 0,
  )
    ? values
    : null
  const total = parsed?.reduce((sum, value) => sum + value, 0) ?? null
  const totalError =
    total != null && total < 1
      ? `The twelve months must add up to at least 1 kWh.`
      : null
  const valid = parsed !== null && !totalError

  return {
    changed,
    valid,
    body: valid && parsed ? { mode: `monthly`, monthlyKwh: parsed } : null,
    monthlyTotalPreview: total,
    errors: { annual: null, months: monthErrors, total: totalError },
  }
}

export const formatKwh = (kwh: number): string =>
  `${Math.round(kwh).toLocaleString(`en-US`)} kWh`

export const formatCapturedDate = (iso: string | null): string | null => {
  if (!iso) return null
  const date = new Date(iso)
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString(`en-US`, {
        month: `short`,
        day: `numeric`,
        year: `numeric`,
      })
}
